import type { FoodOrder, FoodOrderStatus } from "./foodTypes";

// Midlertidig fast konfiguration.
// Senere kan de flyttes til event/settings/API.
export const MICROWAVE_COUNT = 4;
export const MINUTES_PER_BOX = 7;

type EstimateOptions = {
    microwaveOrderIds?: Iterable<number>;
};

function normalizeStatusValue(
    status: FoodOrder["status"]
): FoodOrderStatus {
    if (
        status === "Received" ||
        status === "InProgress" ||
        status === "Ready" ||
        status === "Delivered" ||
        status === "Cancelled"
    ) {
        return status;
    }

    switch (Number(status)) {
        case 0:
            return "Received";

        case 1:
            return "InProgress";

        case 2:
            return "Ready";

        case 3:
            return "Delivered";

        case 4:
            return "Cancelled";

        default:
            return "Received";
    }
}

// Lige nu t�ller quantity som antal bokse.
export function getOrderBoxCount(
    order: FoodOrder
): number {
    return order.lines.reduce(
        (sum, line) =>
            sum +
            Math.max(
                0,
                Number(line.quantity) || 0
            ),
        0
    );
}

export function isFuturePreOrder(
    order: FoodOrder,
    now = new Date()
): boolean {
    if (!order.scheduledFor) {
        return false;
    }

    const scheduled =
        new Date(
            order.scheduledFor
        ).getTime();

    return (
        Number.isFinite(
            scheduled
        ) &&
        scheduled >
        now.getTime()
    );
}

export function isKitchenWorkOrder(
    order: FoodOrder,
    now = new Date()
): boolean {
    const status =
        normalizeStatusValue(
            order.status
        );

    if (
        status === "Ready" ||
        status === "Delivered" ||
        status === "Cancelled"
    ) {
        return false;
    }

    // Fremtidige forudbestillinger
    // bruger ikke kapacitet endnu.
    if (
        status === "Received" &&
        isFuturePreOrder(
            order,
            now
        )
    ) {
        return false;
    }

    return (
        status === "Received" ||
        status === "InProgress"
    );
}

function toIdSet(
    ids?: Iterable<number>
): Set<number> {
    return new Set(
        ids ?? []
    );
}

/**
 * K�-prioritet:
 *
 * 0 = Mikroovn
 * 1 = I gang
 * 2 = Nye ordrer
 */
export function getKitchenPriority(
    order: FoodOrder,
    microwaveOrderIds?: Iterable<number>
): number {
    const microwaveIds =
        toIdSet(
            microwaveOrderIds
        );

    if (
        microwaveIds.has(
            order.id
        )
    ) {
        return 0;
    }

    const status =
        normalizeStatusValue(
            order.status
        );

    if (
        status ===
        "InProgress"
    ) {
        return 1;
    }

    if (
        status ===
        "Received"
    ) {
        return 2;
    }

    return 99;
}

export function sortKitchenQueue(
    orders: FoodOrder[],
    now = new Date(),
    options: EstimateOptions = {}
): FoodOrder[] {
    const microwaveIds =
        toIdSet(
            options.microwaveOrderIds
        );

    return orders
        .filter(order =>
            isKitchenWorkOrder(
                order,
                now
            )
        )
        .sort((a, b) => {
            const aPriority =
                getKitchenPriority(
                    a,
                    microwaveIds
                );

            const bPriority =
                getKitchenPriority(
                    b,
                    microwaveIds
                );

            if (
                aPriority !==
                bPriority
            ) {
                return (
                    aPriority -
                    bPriority
                );
            }

            // Inden for samme gruppe:
            // �ldste ordre f�rst.
            return (
                new Date(
                    a.createdAt
                ).getTime() -
                new Date(
                    b.createdAt
                ).getTime()
            );
        });
}

export function getActiveQueueBoxes(
    orders: FoodOrder[],
    now = new Date(),
    options: EstimateOptions = {}
): number {
    return sortKitchenQueue(
        orders,
        now,
        options
    ).reduce(
        (sum, order) =>
            sum +
            getOrderBoxCount(
                order
            ),
        0
    );
}

export function estimateMinutesForBoxes(
    boxCount: number,
    boxesAhead = 0
): number {
    const boxes =
        Math.max(
            0,
            Math.ceil(
                boxCount
            )
        );

    const ahead =
        Math.max(
            0,
            Math.ceil(
                boxesAhead
            )
        );

    if (boxes === 0) {
        return 0;
    }

    const batches =
        Math.ceil(
            (
                ahead +
                boxes
            ) /
            MICROWAVE_COUNT
        );

    return (
        batches *
        MINUTES_PER_BOX
    );
}

export function estimateNewOrderMinutes(
    currentOrders: FoodOrder[],
    newBoxCount: number,
    now = new Date(),
    options: EstimateOptions = {}
): number {
    const boxesAhead =
        getActiveQueueBoxes(
            currentOrders,
            now,
            options
        );

    return estimateMinutesForBoxes(
        newBoxCount,
        boxesAhead
    );
}

export function estimateOrderMinutes(
    targetOrder: FoodOrder,
    orders: FoodOrder[],
    now = new Date(),
    options: EstimateOptions = {}
): number {
    const targetStatus =
        normalizeStatusValue(
            targetOrder.status
        );

    if (
        targetStatus === "Ready" ||
        targetStatus === "Delivered" ||
        targetStatus === "Cancelled"
    ) {
        return 0;
    }

    if (
        isFuturePreOrder(
            targetOrder,
            now
        )
    ) {
        return estimateMinutesForBoxes(
            getOrderBoxCount(
                targetOrder
            ),
            0
        );
    }

    const activeOrders =
        sortKitchenQueue(
            orders,
            now,
            options
        );

    let boxesAhead = 0;

    for (
        const order
        of activeOrders
    ) {
        if (
            order.id ===
            targetOrder.id
        ) {
            break;
        }

        boxesAhead +=
            getOrderBoxCount(
                order
            );
    }

    return estimateMinutesForBoxes(
        getOrderBoxCount(
            targetOrder
        ),
        boxesAhead
    );
}

export function getEstimatedReadyAt(
    minutes: number,
    from = new Date()
): Date {
    return new Date(
        from.getTime() +
        minutes *
        60 *
        1000
    );
}

export function formatEstimatedTime(
    date: Date
): string {
    return date.toLocaleTimeString(
        "da-DK",
        {
            hour:
                "2-digit",
            minute:
                "2-digit",
        }
    );
}